import { useCounterpartiesFilter, useCounterpartyFilter } from '@src/hooks';
import { usePricingRules } from '@src/providers';
import { BlotterTableOverlay, type GenericFilter } from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';

function getCounterpartyFromFilter(filter: GenericFilter | undefined) {
  const counterpartySelection = filter?.Counterparty ?? filter?.Counterparties;
  if (Array.isArray(counterpartySelection)) {
    return counterpartySelection[0];
  }
  return counterpartySelection;
}

/**
 * Shared setup for pricing-rules pages (Customer Pricing, Customer SubAccount Mappings) that must require a
 * single Counterparty selection before subscribing to pricing rules when `ui.RequireCustomerPricingRulesCounterpartyFilter` is on.
 */
export function useRequiredCustomerFilterProperty(requireCustomerPricingRulesCounterpartyFilter: boolean) {
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer', nullUntilLoaded: true });
  const counterpartiesFilter = useCounterpartiesFilter({ label: 'Customer', nullUntilLoaded: true });

  return useMemo(() => {
    return requireCustomerPricingRulesCounterpartyFilter
      ? counterpartyFilter == null
        ? null
        : { ...counterpartyFilter, alwaysPresent: true }
      : counterpartiesFilter;
  }, [requireCustomerPricingRulesCounterpartyFilter, counterpartyFilter, counterpartiesFilter]);
}

interface RequiredCustomerFilterOptions {
  controlledFilter?: GenericFilter;
  controlledCustomerFilterActive: boolean;
  requireCustomerPricingRulesCounterpartyFilter: boolean;
}

export function useRequiredCustomerFilter({
  controlledFilter,
  controlledCustomerFilterActive,
  requireCustomerPricingRulesCounterpartyFilter,
}: RequiredCustomerFilterOptions) {
  const { pricingRulesCounterpartyFilter, setPricingRulesCounterpartyFilter } = usePricingRules();
  const controlledCounterparty = getCounterpartyFromFilter(controlledFilter);

  const onFilterChanged = useCallback(
    (filter: GenericFilter) => {
      if (!requireCustomerPricingRulesCounterpartyFilter) {
        return;
      }
      setPricingRulesCounterpartyFilter(
        controlledCustomerFilterActive ? controlledCounterparty : getCounterpartyFromFilter(filter)
      );
    },
    [
      controlledCounterparty,
      controlledCustomerFilterActive,
      requireCustomerPricingRulesCounterpartyFilter,
      setPricingRulesCounterpartyFilter,
    ]
  );

  useEffect(() => {
    if (!requireCustomerPricingRulesCounterpartyFilter) {
      return;
    }
    return () => {
      setPricingRulesCounterpartyFilter(undefined);
    };
  }, [requireCustomerPricingRulesCounterpartyFilter, setPricingRulesCounterpartyFilter]);

  useEffect(() => {
    if (requireCustomerPricingRulesCounterpartyFilter && controlledCustomerFilterActive) {
      setPricingRulesCounterpartyFilter(controlledCounterparty);
    }
  }, [
    controlledCounterparty,
    controlledCustomerFilterActive,
    requireCustomerPricingRulesCounterpartyFilter,
    setPricingRulesCounterpartyFilter,
  ]);

  const hasRequiredCounterpartySelection =
    !requireCustomerPricingRulesCounterpartyFilter || pricingRulesCounterpartyFilter != null;

  const blotterTableOverlay = useMemo(() => {
    if (hasRequiredCounterpartySelection) {
      return undefined;
    }
    return (
      <BlotterTableOverlay>
        <span data-testid="select-customer-message">Please select a Customer</span>
      </BlotterTableOverlay>
    );
  }, [hasRequiredCounterpartySelection]);

  return {
    requireCustomerPricingRulesCounterpartyFilter:
      requireCustomerPricingRulesCounterpartyFilter &&
      !(controlledCustomerFilterActive && controlledCounterparty != null),
    onFilterChanged,
    hasRequiredCounterpartySelection,
    blotterTableOverlay,
  };
}
